import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  InlineEditProvider,
  EditModeToolbar,
  EditModeToggleButton,
  NavigationEditor,
  NewPageDialog,
  ThemeSettingsDrawer,
  SiteTheme,
} from '@kevlarcon/pages';
import { TenantBranding } from '../api/auth';
import { useAuth } from '../contexts/AuthContext';
import { BrandPreviewProvider } from '../contexts/BrandPreviewContext';
import { applyOrgTheme } from '../theme/applyOrgTheme';
import SitePagesProvider from '../site/SitePagesProvider';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';
import BrandStyleModal from './BrandStyleModal';

interface Props {
  children: React.ReactNode;
  headerActive?: string;
}

/**
 * The chrome every public, tenant-scoped page shares: header, footer, and —
 * for org admins — a global edit-mode toggle (`EditModeToggleButton`, from
 * `@kevlarcon/pages`) that reveals the CMS toolbar and its editors on demand,
 * so an admin can step back and view the site exactly as a visitor would.
 * InlineEditProvider resolves whichever Page backs the current route on its
 * own (see the package), so this same wrapper works for CMS pages
 * (TenantSite) and for the built-in Donate/Apply routes alike — the latter
 * just won't have inline-editable sections, since they don't render
 * ApiGenericPage.
 */
const SiteAdminChrome: React.FC<Props> = ({ children, headerActive }) => {
  const { isOrgAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [mainNavOpen, setMainNavOpen] = useState(false);
  const [footerNavOpen, setFooterNavOpen] = useState(false);
  const [footer2NavOpen, setFooter2NavOpen] = useState(false);
  const [legalNavOpen, setLegalNavOpen] = useState(false);
  const [newPageOpen, setNewPageOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [brandStyleOpen, setBrandStyleOpen] = useState(false);

  return (
    <SitePagesProvider>
      <InlineEditProvider isStaff={isOrgAdmin}>
        <BrandPreviewProvider>
          <EditModeToggleButton />
          {isOrgAdmin && (
            <EditModeToolbar
              onNewPage={() => setNewPageOpen(true)}
              onThemeSettings={() => setThemeOpen(true)}
              onBrandSettings={() => setBrandStyleOpen(true)}
            />
          )}
          <SiteHeader
            active={headerActive}
            onEditNav={isOrgAdmin ? () => setMainNavOpen(true) : undefined}
          />
          <main>{children}</main>
          <SiteFooter
            onEditNav={isOrgAdmin ? () => setFooterNavOpen(true) : undefined}
            onEditFooter2Nav={isOrgAdmin ? () => setFooter2NavOpen(true) : undefined}
            onEditLegalNav={isOrgAdmin ? () => setLegalNavOpen(true) : undefined}
          />
          {isOrgAdmin && (
            <>
              <NavigationEditor
                open={mainNavOpen}
                onClose={() => setMainNavOpen(false)}
                onSaved={() => queryClient.invalidateQueries({ queryKey: ['site-menu'] })}
                menuLocation="main"
              />
              <NavigationEditor
                open={footerNavOpen}
                onClose={() => setFooterNavOpen(false)}
                onSaved={() => queryClient.invalidateQueries({ queryKey: ['site-menu'] })}
                menuLocation="footer"
              />
              <NavigationEditor
                open={footer2NavOpen}
                onClose={() => setFooter2NavOpen(false)}
                onSaved={() => queryClient.invalidateQueries({ queryKey: ['site-menu'] })}
                menuLocation="footer_2"
              />
              <NavigationEditor
                open={legalNavOpen}
                onClose={() => setLegalNavOpen(false)}
                onSaved={() => queryClient.invalidateQueries({ queryKey: ['site-menu'] })}
                menuLocation="legal"
              />
              <NewPageDialog open={newPageOpen} onClose={() => setNewPageOpen(false)} />
              <ThemeSettingsDrawer
                open={themeOpen}
                onClose={() => setThemeOpen(false)}
                onPreview={(preview: SiteTheme) => {
                  const current = queryClient.getQueryData<TenantBranding>(['tenant_branding']);
                  if (current) applyOrgTheme({ ...current, ...preview });
                }}
                onSaved={(saved: SiteTheme) => queryClient.setQueryData<TenantBranding>(
                  ['tenant_branding'],
                  (old) => (old ? { ...old, ...saved } : old),
                )}
              />
              <BrandStyleModal open={brandStyleOpen} onClose={() => setBrandStyleOpen(false)} />
            </>
          )}
        </BrandPreviewProvider>
      </InlineEditProvider>
    </SitePagesProvider>
  );
};

export default SiteAdminChrome;
